/**
 * Rebuild the builder's internal model from WhatsApp Flow JSON.
 *
 * Moved verbatim out of the load `useEffect` in `hooks/useFlowBuilder.js`. The
 * hook keeps the React state plumbing (which flow is loaded, undo baseline,
 * active screen); everything that turns JSON into screens lives here so the
 * backend can round-trip a flow without a browser.
 *
 * `compile(hydrate(json))` must be idempotent, that property is what the
 * differential tests assert, and it is why the positional `_builderMeta`
 * component-condition keys must be read back exactly as they were written.
 */

import { generateId, generateAlphabeticalSuffix } from './idGeneration';
import { conditionToIfExpression, ruleToIfExpression } from './conditionUtils';
import { isRoutingSwitch } from './routingSwitch';
import { flowComponentRegistry } from './FlowComponentRegistry';
import { ensureFootersOnAllScreens } from './footerManager';
import { defaultRouting, ensureBranchIds } from './builderModel';
import { BOOKING_SCREEN_IDS } from './booking';

/**
 * Collapse the routing If-chain back into the single Footer the builder edits.
 *
 * When a screen has routing rules, `compile` removes its Footer and appends a
 * nested If chain (one level per rule, `buildIfTree`). Without the inverse, that
 * chain loaded as a literal If component: the screen then had no Footer, so
 * `ensureFootersOnAllScreens` added a second one, and the next save emitted
 * both the old chain and a freshly built one, the flow grew a duplicate
 * navigation block on every open-and-save cycle.
 *
 * Symmetric to `findAutoWrappedCondition`: we only unwrap a structure we would
 * have written ourselves, the chain has to be the last child, have exactly one
 * level per valid rule, carry the same condition strings in the same order, and
 * bottom out in a Footer. Anything else is a user-authored If and is left alone.
 */
const unwrapRoutingFooter = (children, routing) => {
  const rules = (routing?.rules || []).filter((rule) => ruleToIfExpression(rule) && rule.nextScreenId);
  if (rules.length === 0 || children.length === 0) {
    return children;
  }

  let node = children[children.length - 1];
  // Many-way routing compiles to a Switch of Footers (routingSwitch.js); the rules themselves
  // travel in _builderMeta, so the default Footer is all the builder needs back.
  if (isRoutingSwitch(node)) {
    const branches = Object.values(node.cases);
    const fallback = branches.find((branch) => branch[0]['on-click-action']?.next?.name === routing.defaultNextScreenId) || branches[branches.length - 1];
    return [...children.slice(0, -1), fallback[0]];
  }
  for (const rule of rules) {
    if (!node || node.type !== 'If') return children;
    if (node.condition !== ruleToIfExpression(rule)) return children;
    if (!Array.isArray(node.then) || node.then.length !== 1 || node.then[0].type !== 'Footer') return children;
    if (!Array.isArray(node.else) || node.else.length !== 1) return children;
    node = node.else[0];
  }
  if (!node || node.type !== 'Footer') {
    return children;
  }

  // `node` is the default-branch Footer compile emitted for the screen's
  // defaultNextScreenId, the right thing to hand back to the builder, since
  // compile rebuilds the whole chain from the rules on the next export.
  return [...children.slice(0, -1), node];
};

/** A default route read off the screen's own Footer when the metadata has none. */
const routingFromFooter = (screen, screens, index) => {
  const children = screen.layout?.children || [];
  const footer = children[children.length - 1];
  const target = footer && footer.type === 'Footer' ? footer['on-click-action']?.next?.name : null;
  const nextInList = index + 1 < screens.length ? screens[index + 1].id : null;
  if (!target || target === nextInList || !screens.some((s) => s.id === target)) return defaultRouting();
  return { ...defaultRouting(), defaultNextScreenId: target };
};

/**
 * The screens a blank builder starts from (also the fallback for a flow whose
 * JSON carries no usable screens, e.g. one synced from WhatsApp without the
 * full definition).
 */
export const blankScreens = (title = 'Welcome Screen') =>
  ensureFootersOnAllScreens([
    {
      id: 'screen_a',
      title,
      components: [],
      terminal: false,
      routing: defaultRouting(),
    },
  ]);

/** True when a Flow JSON has screens we can actually load. */
export const hasLoadableScreens = (flowJson) =>
  !!flowJson && Array.isArray(flowJson.screens) && flowJson.screens.length > 0;

/**
 * True when a flow carries nothing the author put there.
 *
 * A blank canvas is not an empty document: it compiles to one screen with an auto-added
 * Footer, so `hasLoadableScreens` is true and anything asking "is there a flow here?" gets
 * yes. The assistant needs the other answer, it should offer its empty state on a new flow,
 * and must not hand a placeholder screen to the model as the flow to edit.
 *
 * Footers are ignored because the compiler adds them; the author never does.
 *
 * @param {Object|null} flowJson
 * @returns {boolean}
 */
export const isBlankFlow = (flowJson) => {
  if (!hasLoadableScreens(flowJson)) return true;
  return flowJson.screens.every((screen) => {
    const children = (screen.layout && screen.layout.children) || [];
    return children.every((child) => child && child.type === 'Footer');
  });
};

/**
 * @param {Object|null} flowJson
 * @param {{ fallbackTitle?: string }} [options]
 * @returns {Array} the builder's screens model, with Footers ensured
 */
export const hydrate = (flowJson, options = {}) => {
  if (!hasLoadableScreens(flowJson)) {
    return blankScreens(options.fallbackTitle || 'Welcome Screen');
  }

  // Convert FlowJSON back to internal format
  const builderMeta = flowJson._builderMeta || {};
  const savedScreenRouting = builderMeta.screenRouting || {};
  const savedComponentConditions = builderMeta.componentConditions || {};

  const loadedScreens = flowJson.screens.map((screen, screenIndex) => {
    // Generate proper screen ID if it doesn't follow our format
    // The booking flow's screen ids (SERVICE, DATE, …) are the runtime's contract, not a
    // builder convention, renaming them to screen_a would silently break every live
    // booking. Everything else is still normalised to the builder's own id shape. A language
    // copy made by the generator (screen_b_fa, screen_e_end_ku) keeps its suffixes: renaming it
    // to screen_c left the language routing pointing at a screen that no longer existed
    // (2026-09-18, twice: first the language suffix, then a planner's own "_end" suffix).
    const screenId = screen.id.match(/^screen_[a-z][a-z_]*$/) || BOOKING_SCREEN_IDS.includes(screen.id)
      ? screen.id
      : `screen_${generateAlphabeticalSuffix(screenIndex)}`;

    // Auto-wrapped conditionals (see compile): a single-child If
    // with no else is the export-time wrapper for a builder-level
    // _condition. We discriminate user-authored Ifs from auto-wrappers via
    // (a) a positional metadata entry written by the export pass, AND
    // (b) the If's condition string matching what we'd have written
    // ourselves. Both must match, name-only matching is too loose and
    // condition-only is too coincidental.
    //
    // For backwards compatibility we also fall back to the legacy
    // name-keyed metadata when no positional entry exists.
    const findAutoWrappedCondition = (component, childIndex) => {
      if (component.type !== 'If') return null;
      if (!Array.isArray(component.then) || component.then.length !== 1) return null;
      if (Array.isArray(component.else) && component.else.length > 0) return null;

      const positionalKey = `${screenId}:${childIndex}`;
      const inner = component.then[0];
      const nameKey = inner?.name ? `${screenId}:${inner.name}` : null;

      const candidate = savedComponentConditions[positionalKey]
        || (nameKey ? savedComponentConditions[nameKey] : null);
      if (!candidate) return null;

      if (component.condition !== conditionToIfExpression(candidate)) return null;
      return candidate;
    };

    // Saved routing first; failing that, a Footer that navigates somewhere other than the next
    // screen in the list is a default route (a flow built elsewhere, or saved before defaults
    // were kept in the metadata).
    const routing = savedScreenRouting[screenId] || routingFromFooter(screen, flowJson.screens, screenIndex);

    return {
      id: screenId,
      title: screen.title,
      terminal: screen.terminal || false,
      // The data-exchange declaration is authored, not conferred: carried through so a
      // dynamic flow survives an open-and-save in the builder.
      ...(screen.data && Object.keys(screen.data).length > 0 ? { data: screen.data } : {}),
      routing,
      components: unwrapRoutingFooter(screen.layout?.children || [], routing).map((rawComponent, childIndex) => {
        const restoredCondition = findAutoWrappedCondition(rawComponent, childIndex);
        const component = restoredCondition ? rawComponent.then[0] : rawComponent;
        // Every registered component restores builder format via
        // transformFromWhatsApp (the base class falls back to a plain
        // property spread). This matters for the selection components,
        // whose dynamic `data-source` is a `${data.x}` STRING in the JSON
        //, raw-spreading that into builder props crashes every consumer
        // that assumes `data-source` is an array. Containers (If, Switch)
        // additionally need branch ids regenerated.
        const registeredComp = flowComponentRegistry.get(component.type);
        const isContainer = component.type === 'If' || component.type === 'Switch';
        let props;
        if (registeredComp && isContainer) {
          props = ensureBranchIds(registeredComp.transformFromWhatsApp(component));
        } else if (registeredComp) {
          props = registeredComp.transformFromWhatsApp(component);
        } else {
          props = { ...component };
        }
        // Restore _condition ONLY on the unwrapped path so a stale
        // metadata entry never gets glued onto an unrelated component
        // that happens to share a name with the deleted conditional.
        if (restoredCondition) {
          props._condition = restoredCondition;
        }
        return {
          id: generateId(),
          type: component.type,
          properties: props,
        };
      }),
    };
  });

  return ensureFootersOnAllScreens(loadedScreens);
};
